import React, { useState, useMemo, useEffect } from 'react';
import { useTheme } from '@/theme';
import * as miniLogApi from '@/api/device/logs/miniLog';
import * as liteLogApi from '@/api/device/logs/liteLog';
import { formatDateTime } from '@/utils/date';
import { SENSOR_TYPE_MAP } from '@packages/types/device/lite/sensors';
import { getSensorTypeInfo } from '@packages/types/device/lite/lite_constraints';
import {
  TabLayout,
  TabHeader,
  ErrorMessage,
  EmptyState,
  LoadingSkeleton,
  DownloadButtons,
} from '@/components/layout/TabLayout';
import * as XLSX from 'xlsx';
import { useLogTabState, useLogFetch } from './hooks/useLogTabState';

interface Device {
  serial_number: string;
  device_type?: string;
  sensor_types?: string[]; // Lite 센서 타입 배열
}

interface ConfigurationLogTabProps {
  device: Device;
  onExportData?: (data: {
    tableData: any[];
    tableColumns: Array<{ header: string; accessorKey: string }>;
  }) => void;
  onDownloadPdf?: () => Promise<void>;
}

interface ConfigurationLog {
  id: number;
  log_id: string;
  serial_number: string;
  record_index: number;
  timestamp: string;
  log_type: number;
  sensor_num?: number | null;
  sensor?: string | null; // Mini: parsed sensor name from sensor_num
  sensor_number?: number | null; // Lite는 sensor_number 사용
  // Sensor Config 필드들 (Lite)
  sensor_type?: number | null;
  sensor_type_text?: string | null;
  decimal_point?: number | null;
  auto_zero_option?: boolean;
  sensor_disabled?: boolean;
  conc_unit?: number | null;
  conc_unit_text?: string | null;
  lel_cf_index?: number | null;
  lel_cf_text?: string | null;
  user_lel_correction_factor?: number | null;
  vol_for_50_lel?: number | null;
  vol_for_50_lel_text?: string | null;
  bump_test_threshold?: number | null;
  // 공통 필드들
  cal_gas_conc?: number | null;
  confidence_beep_interval?: number | null;
  green_light_interval?: number | null;
  datalog_interval?: number | null;
  cal_interval?: number | null;
  bump_interval?: number | null;
  stel_period?: number | null;
  twa_period?: number | null;
  low_alarm_threshold?: number | null;
  high_alarm_threshold?: number | null;
  twa_threshold?: number | null;
  stel_threshold?: number | null;
  settings_options?: number | null;
  worker_name?: string | null;
  location_name?: string | null;
  stealth_mode: boolean;
  safe_mode: boolean;
  enable_confidence_beep: boolean;
  enable_green_light: boolean;
  alarm_latch: boolean;
  enable_ble: boolean;
  low_alarm_ack: boolean;
  auto_zero_at_startup: boolean;
  enable_2nd_span_cal?: boolean;
}

interface GeneralConfigData {
  worker_name?: string;
  location_name?: string;
  sensor?: string; // Mini only
  stealth_mode: string;
  safe_mode: string;
  latching_alarm: string;
  confidence_beep: string;
  green_light: string;
  datalog_interval: string;
  confidence_beep_interval: string;
  green_light_interval: string;
  ble: string;
  low_alarm_ack: string;
  second_span_cal: string;
  // Mini-specific sensor config fields
  cal_gas_conc?: string;
  cal_interval?: string;
  bump_interval?: string;
  stel_period?: string;
  twa_period?: string;
  low_alarm_threshold?: string;
  high_alarm_threshold?: string;
  twa_threshold?: string;
  stel_threshold?: string;
  auto_zero_at_startup?: string;
}

interface SensorConfigData {
  sensor_type_text: string;
  disabled: string;
  auto_zero_option: string;
  lel_cf_text: string;
  user_lel_correction_factor: string;
  cal_interval: string;
  bump_interval: string;
  cal_gas_conc: string;
  stel_period: string;
  twa_period: string;
  low_alarm_threshold: string;
  high_alarm_threshold: string;
  twa_threshold: string;
  stel_threshold: string;
  vol_for_50_lel_text: string;
  bump_test_threshold: string;
}

interface GroupedConfigLog {
  timestamp: string;
  timestamp_formatted: string;
  general?: GeneralConfigData;
  sensors: SensorConfigData[];
}

export const ConfigurationLogTab: React.FC<ConfigurationLogTabProps> = ({
  device,
  onExportData,
  onDownloadPdf: _onDownloadPdf,
}) => {
  const { theme } = useTheme();

  const {
    loading, setLoading,
    error, setError, handleError,
    dateRange, setDateRange,
    isLite,
    shouldFetch,
  } = useLogTabState({ device });

  const [logs, setLogs] = useState<ConfigurationLog[]>([]);

  const fetchConfigurationLogs = async () => {
    if (!device.serial_number || !dateRange.from || !dateRange.to) return;

    setLoading(true);
    setError(null);

    try {
      if (isLite) {
        // Lite: 백엔드에서 이미 timestamp로 그룹화된 데이터 가져오기
        const groupedData = await liteLogApi.getConfigurationLogsByDevice(
          device.serial_number,
          dateRange.from,
          dateRange.to,
          10000
        );

        // 그룹화된 데이터를 ConfigurationLog 형식으로 플랫하게 변환
        const combinedLogs: ConfigurationLog[] = [];

        groupedData.forEach(group => {
          // General Config 추가
          if (group.general) {
            const generalData = group.general as any;
            combinedLogs.push({
              id: generalData.id,
              log_id: generalData.log_id,
              serial_number: generalData.serial_number,
              record_index: generalData.record_index,
              timestamp: group.timestamp,
              log_type: 2,
              sensor_num: null,
              worker_name: generalData.worker_name,
              location_name: generalData.location_name,
              confidence_beep_interval: generalData.confidence_beep_interval,
              green_light_interval: generalData.green_light_interval,
              datalog_interval: generalData.datalog_interval,
              stealth_mode: generalData.stealth_mode,
              safe_mode: generalData.safe_mode,
              enable_confidence_beep: generalData.enable_confidence_beep,
              enable_green_light: generalData.enable_green_light,
              alarm_latch: generalData.alarm_latch || false,
              enable_ble: generalData.enable_ble,
              low_alarm_ack: generalData.low_alarm_ack || false,
              auto_zero_at_startup: generalData.auto_zero_at_startup || false,
              enable_2nd_span_cal: generalData.enable_2nd_span_cal || false,
            } as ConfigurationLog);
          }

          // Sensor Config 추가
          group.sensors.forEach((sensor: unknown) => {
            const sensorData = sensor as any;
            combinedLogs.push({
              id: sensorData.id,
              log_id: sensorData.log_id,
              serial_number: sensorData.serial_number,
              record_index: sensorData.record_index,
              timestamp: group.timestamp,
              log_type: 3,
              sensor_num: sensorData.sensor_number,
              sensor_number: sensorData.sensor_number,
              sensor_type: sensorData.sensor_type,
              sensor_type_text: sensorData.sensor_type_text,
              decimal_point: sensorData.decimal_point,
              auto_zero_option: sensorData.auto_zero_option,
              sensor_disabled: sensorData.sensor_disabled,
              conc_unit: sensorData.conc_unit,
              conc_unit_text: sensorData.conc_unit_text,
              lel_cf_index: sensorData.lel_cf_index,
              lel_cf_text: sensorData.lel_cf_text,
              user_lel_correction_factor: sensorData.user_lel_correction_factor,
              vol_for_50_lel: sensorData.vol_for_50_lel,
              vol_for_50_lel_text: sensorData.vol_for_50_lel_text,
              bump_test_threshold: sensorData.bump_test_threshold,
              cal_gas_conc: sensorData.cal_gas_conc,
              cal_interval: sensorData.cal_interval,
              bump_interval: sensorData.bump_interval,
              stel_period: sensorData.stel_period,
              twa_period: sensorData.twa_period,
              low_alarm_threshold: sensorData.low_alarm_threshold,
              high_alarm_threshold: sensorData.high_alarm_threshold,
              twa_threshold: sensorData.twa_threshold,
              stel_threshold: sensorData.stel_threshold,
              stealth_mode: false,
              safe_mode: false,
              enable_confidence_beep: false,
              enable_green_light: false,
              alarm_latch: false,
              enable_ble: false,
              low_alarm_ack: false,
              auto_zero_at_startup: false,
            } as ConfigurationLog);
          });
        });

        setLogs(combinedLogs);
      } else {
        // Mini: 기존 로직 유지
        const data = await miniLogApi.getConfigurationLogsByDevice(
          device.serial_number,
          dateRange.from,
          dateRange.to,
          10000
        );

        setLogs(data);
      }
    } catch (err: unknown) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  useLogFetch({
    shouldFetch,
    deps: [device.serial_number, device.device_type, dateRange.from, dateRange.to],
    fetchFn: fetchConfigurationLogs,
  });

  const formatDateTimeStr = (dateString: string): string => {
    return formatDateTime(dateString);
  };

  // 센서 타입 텍스트로 features 가져오기
  const getSensorFeatures = (sensorTypeText: string | null) => {
    const defaultFeatures = {
      has_twa_stel: false,
      enable_bump_test_threshold: false,
      enable_auto_fresh: false,
      enable_correction_factor: false,
    };

    if (!sensorTypeText || !SENSOR_TYPE_MAP[sensorTypeText]) {
      return defaultFeatures;
    }

    const sensorType = SENSOR_TYPE_MAP[sensorTypeText];
    const sensorInfo = getSensorTypeInfo(sensorType);

    return {
      has_twa_stel: sensorInfo.has_twa_stel,
      enable_bump_test_threshold: sensorInfo.enable_bump_test_threshold,
      enable_auto_fresh: sensorInfo.enable_auto_fresh,
      enable_correction_factor: sensorInfo.enable_correction_factor,
    };
  };

  const groupedLogs = useMemo<GroupedConfigLog[]>(() => {
    if (isLite) {
      // LITE: timestamp로 그룹화
      const groupedByTimestamp = new Map<string, ConfigurationLog[]>();

      logs.forEach(log => {
        const existing = groupedByTimestamp.get(log.timestamp) || [];
        existing.push(log);
        groupedByTimestamp.set(log.timestamp, existing);
      });

      const result: GroupedConfigLog[] = [];

      Array.from(groupedByTimestamp.entries())
        .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
        .forEach(([timestamp, logsGroup]) => {
          const generalConfig = logsGroup.find(log => log.sensor_num === null);
          const sensorConfigs = logsGroup
            .filter(log => log.sensor_num !== null)
            .sort((a, b) => (a.sensor_num || 0) - (b.sensor_num || 0));

          const group: GroupedConfigLog = {
            timestamp,
            timestamp_formatted: formatDateTimeStr(timestamp),
            sensors: [],
          };

          // General 데이터
          if (generalConfig) {
            group.general = {
              worker_name: generalConfig.worker_name || '-',
              location_name: generalConfig.location_name || '-',
              stealth_mode: generalConfig.stealth_mode ? 'ON' : 'OFF',
              safe_mode: generalConfig.safe_mode ? 'ON' : 'OFF',
              latching_alarm: generalConfig.alarm_latch ? 'ON' : 'OFF',
              confidence_beep: generalConfig.enable_confidence_beep ? 'ON' : 'OFF',
              green_light: generalConfig.enable_green_light ? 'ON' : 'OFF',
              datalog_interval:
                generalConfig.datalog_interval !== null
                  ? `${generalConfig.datalog_interval}s`
                  : '-',
              confidence_beep_interval:
                generalConfig.confidence_beep_interval !== null
                  ? `${generalConfig.confidence_beep_interval}s`
                  : '-',
              green_light_interval:
                generalConfig.green_light_interval !== null
                  ? `${generalConfig.green_light_interval}s`
                  : '-',
              ble: generalConfig.enable_ble ? 'ON' : 'OFF',
              low_alarm_ack: generalConfig.low_alarm_ack ? 'ON' : 'OFF',
              second_span_cal: generalConfig.enable_2nd_span_cal ? 'ON' : 'OFF',
            };
          }

          // Sensor 데이터들
          sensorConfigs.forEach(sensorConfig => {
            const sensorTypeText = sensorConfig.sensor_type_text || null;
            if (!sensorTypeText) return;
            const features = getSensorFeatures(sensorTypeText);

            group.sensors.push({
              sensor_type_text: sensorTypeText || '-',
              disabled: sensorConfig.sensor_disabled ? 'YES' : 'NO',
              auto_zero_option: sensorConfig.auto_zero_option ? 'ON' : 'OFF',
              lel_cf_text: features.enable_correction_factor
                ? sensorConfig.lel_cf_text || '-'
                : '-',
              user_lel_correction_factor: features.enable_correction_factor
                ? sensorConfig.user_lel_correction_factor !== null &&
                  sensorConfig.user_lel_correction_factor !== undefined
                  ? String(sensorConfig.user_lel_correction_factor)
                  : '-'
                : '-',
              cal_interval:
                sensorConfig.cal_interval !== null ? `${sensorConfig.cal_interval} days` : '-',
              bump_interval:
                sensorConfig.bump_interval !== null ? `${sensorConfig.bump_interval} days` : '-',
              cal_gas_conc:
                sensorConfig.cal_gas_conc !== null ? String(sensorConfig.cal_gas_conc) : '-',
              stel_period: features.has_twa_stel
                ? sensorConfig.stel_period !== null
                  ? `${sensorConfig.stel_period} min`
                  : '-'
                : '-',
              twa_period: features.has_twa_stel
                ? sensorConfig.twa_period !== null
                  ? `${sensorConfig.twa_period} h`
                  : '-'
                : '-',
              low_alarm_threshold:
                sensorConfig.low_alarm_threshold !== null
                  ? String(sensorConfig.low_alarm_threshold)
                  : '-',
              high_alarm_threshold:
                sensorConfig.high_alarm_threshold !== null
                  ? String(sensorConfig.high_alarm_threshold)
                  : '-',
              twa_threshold: features.has_twa_stel
                ? sensorConfig.twa_threshold !== null
                  ? String(sensorConfig.twa_threshold)
                  : '-'
                : '-',
              stel_threshold: features.has_twa_stel
                ? sensorConfig.stel_threshold !== null
                  ? String(sensorConfig.stel_threshold)
                  : '-'
                : '-',
              vol_for_50_lel_text: features.enable_correction_factor
                ? sensorConfig.vol_for_50_lel_text || '-'
                : '-',
              bump_test_threshold: features.enable_bump_test_threshold
                ? sensorConfig.bump_test_threshold !== null &&
                  sensorConfig.bump_test_threshold !== undefined
                  ? `${sensorConfig.bump_test_threshold}%`
                  : '-'
                : '-',
            });
          });

          result.push(group);
        });

      return result;
    } else {
      // MINI: 각 로그를 개별 그룹으로
      return logs.map(log => ({
        timestamp: log.timestamp,
        timestamp_formatted: formatDateTimeStr(log.timestamp),
        general: {
          sensor: log.sensor || '-',
          stealth_mode: log.stealth_mode ? 'ON' : 'OFF',
          safe_mode: log.safe_mode ? 'ON' : 'OFF',
          latching_alarm: log.alarm_latch ? 'ON' : 'OFF',
          confidence_beep: log.enable_confidence_beep ? 'ON' : 'OFF',
          green_light: log.enable_green_light ? 'ON' : 'OFF',
          datalog_interval: log.datalog_interval != null ? `${log.datalog_interval}s` : '-',
          confidence_beep_interval:
            log.confidence_beep_interval != null ? `${log.confidence_beep_interval}s` : '-',
          green_light_interval:
            log.green_light_interval != null ? `${log.green_light_interval}s` : '-',
          ble: log.enable_ble ? 'ON' : 'OFF',
          low_alarm_ack: log.low_alarm_ack ? 'ON' : 'OFF',
          second_span_cal: log.enable_2nd_span_cal ? 'ON' : 'OFF',
          cal_gas_conc: log.cal_gas_conc != null ? String(log.cal_gas_conc) : '-',
          cal_interval: log.cal_interval != null ? `${log.cal_interval} days` : '-',
          bump_interval: log.bump_interval != null ? `${log.bump_interval} days` : '-',
          stel_period: log.stel_period != null ? `${log.stel_period} min` : '-',
          twa_period: log.twa_period != null ? `${log.twa_period} h` : '-',
          low_alarm_threshold:
            log.low_alarm_threshold != null ? String(log.low_alarm_threshold) : '-',
          high_alarm_threshold:
            log.high_alarm_threshold != null ? String(log.high_alarm_threshold) : '-',
          twa_threshold: log.twa_threshold != null ? String(log.twa_threshold) : '-',
          stel_threshold: log.stel_threshold != null ? String(log.stel_threshold) : '-',
          auto_zero_at_startup: log.auto_zero_at_startup ? 'ON' : 'OFF',
        },
        sensors: [],
      }));
    }
  }, [logs, isLite]);

  // 각 그룹을 바로 이전(시간상 더 오래된) 그룹과 비교해 변경된 필드를 표시한다.
  // groupedLogs는 최신순(내림차순) 정렬이므로 index+1이 이전 설정이다.
  const changeFlags = useMemo(() => {
    return groupedLogs.map((group, idx) => {
      const prev = groupedLogs[idx + 1];
      const generalChanged: Record<string, boolean> = {};
      const sensorsChanged: Array<Record<string, boolean>> = [];

      if (group.general && prev?.general) {
        (Object.keys(group.general) as Array<keyof GeneralConfigData>).forEach(key => {
          if (group.general![key] !== prev.general![key]) {
            generalChanged[key as string] = true;
          }
        });
      }

      group.sensors.forEach(sensor => {
        const changed: Record<string, boolean> = {};
        const prevSensor = prev?.sensors.find(
          s => s.sensor_type_text === sensor.sensor_type_text
        );
        if (prevSensor) {
          (Object.keys(sensor) as Array<keyof SensorConfigData>).forEach(key => {
            if (sensor[key] !== prevSensor[key]) {
              changed[key as string] = true;
            }
          });
        }
        sensorsChanged.push(changed);
      });

      return { general: generalChanged, sensors: sensorsChanged };
    });
  }, [groupedLogs]);

  // 변경된 값은 강조색 + 볼드로 표시한다.
  const renderValue = (value: React.ReactNode, changed?: boolean): React.ReactNode =>
    changed ? (
      <span
        style={{ color: theme.colors.status.caution.stroke.text, fontWeight: 700 }}
      >
        {value}
      </span>
    ) : (
      value
    );

  useEffect(() => {
    if (!onExportData) return;
    if (loading) return;
    if (logs.length === 0) {
      onExportData({
        tableData: [],
        tableColumns: [],
      });
      return;
    }

    // Export data 준비
    const tableData: any[] = [];
    const tableColumns: Array<{ header: string; accessorKey: string }> = [];

    if (isLite) {
      // Lite: 화면과 동일한 구조로 export
      tableColumns.push(
        { header: 'Time', accessorKey: 'Time' },
        { header: 'Section', accessorKey: 'Section' }
      );

      groupedLogs.forEach(group => {
        // General 섹션
        if (group.general) {
          const generalRow: any = {
            Time: group.timestamp_formatted,
            Section: 'General',
            Worker: group.general.worker_name,
            Location: group.general.location_name,
            'Stealth Mode': group.general.stealth_mode,
            'Safe Mode': group.general.safe_mode,
            'Latching Alarm': group.general.latching_alarm,
            'Confidence Beep': group.general.confidence_beep,
            'Green Light': group.general.green_light,
            'Datalog Interval': group.general.datalog_interval,
            'Confidence Beep Interval': group.general.confidence_beep_interval,
            'Green Light Interval': group.general.green_light_interval,
            BLE: group.general.ble,
            'Low Alarm Ack': group.general.low_alarm_ack,
            '2nd Span Cal': group.general.second_span_cal,
          };
          tableData.push(generalRow);
        }

        // Sensor 섹션
        group.sensors.forEach(sensor => {
          const sensorRow: any = {
            Time: group.timestamp_formatted,
            Section: 'Sensors',
            Sensor: sensor.sensor_type_text,
            Disabled: sensor.disabled,
            'Cal Gas Conc': sensor.cal_gas_conc,
            'Cal Interval': sensor.cal_interval,
            'Bump Interval': sensor.bump_interval,
            'Low Alarm': sensor.low_alarm_threshold,
            'High Alarm': sensor.high_alarm_threshold,
            'Auto Zero': sensor.auto_zero_option,
            'LEL CF': sensor.lel_cf_text,
            'User LEL CF': sensor.user_lel_correction_factor,
            'TWA Alarm': sensor.twa_threshold,
            'STEL Alarm': sensor.stel_threshold,
            'TWA Period': sensor.twa_period,
            'STEL Period': sensor.stel_period,
            'Vol for 50% LEL': sensor.vol_for_50_lel_text,
            'Bump Test Threshold': sensor.bump_test_threshold,
          };
          tableData.push(sensorRow);
        });
      });

      // 컬럼 정의 (General + Sensor 모든 필드)
      if (tableData.length > 0) {
        const allKeys = new Set<string>();
        tableData.forEach(row => {
          Object.keys(row).forEach(key => allKeys.add(key));
        });
        tableColumns.length = 0;
        allKeys.forEach(key => {
          tableColumns.push({ header: key, accessorKey: key });
        });
      }
    } else {
      // Mini
      tableColumns.push(
        { header: 'Time', accessorKey: 'Time' },
        { header: 'Sensor', accessorKey: 'Sensor' },
        { header: 'Stealth Mode', accessorKey: 'Stealth Mode' },
        { header: 'Safe Mode', accessorKey: 'Safe Mode' },
        { header: 'Latching Alarm', accessorKey: 'Latching Alarm' },
        { header: 'Confidence Beep', accessorKey: 'Confidence Beep' },
        { header: 'Green Light', accessorKey: 'Green Light' },
        { header: 'Datalog Interval', accessorKey: 'Datalog Interval' },
        { header: 'Confidence Beep Interval', accessorKey: 'Confidence Beep Interval' },
        { header: 'Green Light Interval', accessorKey: 'Green Light Interval' },
        { header: 'BLE', accessorKey: 'BLE' },
        { header: 'Low Alarm Ack', accessorKey: 'Low Alarm Ack' },
        { header: '2nd Span Cal', accessorKey: '2nd Span Cal' },
        { header: 'Cal Gas Conc', accessorKey: 'Cal Gas Conc' },
        { header: 'Cal Interval', accessorKey: 'Cal Interval' },
        { header: 'Bump Interval', accessorKey: 'Bump Interval' },
        { header: 'STEL Period', accessorKey: 'STEL Period' },
        { header: 'TWA Period', accessorKey: 'TWA Period' },
        { header: 'Low Alarm', accessorKey: 'Low Alarm' },
        { header: 'High Alarm', accessorKey: 'High Alarm' },
        { header: 'TWA Alarm', accessorKey: 'TWA Alarm' },
        { header: 'STEL Alarm', accessorKey: 'STEL Alarm' },
        { header: 'Auto Zero at Startup', accessorKey: 'Auto Zero at Startup' }
      );

      groupedLogs.forEach(group => {
        if (group.general) {
          tableData.push({
            Time: group.timestamp_formatted,
            Sensor: group.general.sensor,
            'Stealth Mode': group.general.stealth_mode,
            'Safe Mode': group.general.safe_mode,
            'Latching Alarm': group.general.latching_alarm,
            'Confidence Beep': group.general.confidence_beep,
            'Green Light': group.general.green_light,
            'Datalog Interval': group.general.datalog_interval,
            'Confidence Beep Interval': group.general.confidence_beep_interval,
            'Green Light Interval': group.general.green_light_interval,
            BLE: group.general.ble,
            'Low Alarm Ack': group.general.low_alarm_ack,
            '2nd Span Cal': group.general.second_span_cal,
            'Cal Gas Conc': group.general.cal_gas_conc,
            'Cal Interval': group.general.cal_interval,
            'Bump Interval': group.general.bump_interval,
            'STEL Period': group.general.stel_period,
            'TWA Period': group.general.twa_period,
            'Low Alarm': group.general.low_alarm_threshold,
            'High Alarm': group.general.high_alarm_threshold,
            'TWA Alarm': group.general.twa_threshold,
            'STEL Alarm': group.general.stel_threshold,
            'Auto Zero at Startup': group.general.auto_zero_at_startup,
          });
        }
      });
    }

    onExportData({
      tableData,
      tableColumns,
    });
  }, [onExportData, logs, groupedLogs, loading, isLite]);

  // Excel 다운로드 핸들러
  const handleExcelDownload = () => {
    if (logs.length === 0) return;

    const excelData: any[] = [];

    if (isLite) {
      // Lite: 화면 구조 그대로 export
      groupedLogs.forEach(group => {
        // General 섹션
        if (group.general) {
          excelData.push({
            Time: group.timestamp_formatted,
            Section: 'General',
            'Field 1': 'Worker',
            'Value 1': group.general.worker_name,
            'Field 2': 'Location',
            'Value 2': group.general.location_name,
            'Field 3': 'Stealth Mode',
            'Value 3': group.general.stealth_mode,
            'Field 4': 'Safe Mode',
            'Value 4': group.general.safe_mode,
            'Field 5': 'Latching Alarm',
            'Value 5': group.general.latching_alarm,
          });
          excelData.push({
            Time: '',
            Section: '',
            'Field 1': 'Confidence Beep',
            'Value 1': group.general.confidence_beep,
            'Field 2': 'Green Light',
            'Value 2': group.general.green_light,
            'Field 3': 'Datalog Interval',
            'Value 3': group.general.datalog_interval,
            'Field 4': 'Confidence Beep Interval',
            'Value 4': group.general.confidence_beep_interval,
            'Field 5': 'Green Light Interval',
            'Value 5': group.general.green_light_interval,
          });
          excelData.push({
            Time: '',
            Section: '',
            'Field 1': 'BLE',
            'Value 1': group.general.ble,
            'Field 2': 'Low Alarm Ack',
            'Value 2': group.general.low_alarm_ack,
            'Field 3': '2nd Span Cal',
            'Value 3': group.general.second_span_cal,
            'Field 4': '',
            'Value 4': '',
            'Field 5': '',
            'Value 5': '',
          });
        }

        // Sensor 섹션
        group.sensors.forEach((sensor, idx) => {
          excelData.push({
            Time: idx === 0 ? group.timestamp_formatted : '',
            Section: idx === 0 ? 'Sensors' : '',
            Sensor: sensor.sensor_type_text,
            Disabled: sensor.disabled,
            'Cal Gas Conc': sensor.cal_gas_conc,
            'Cal Interval': sensor.cal_interval,
            'Bump Interval': sensor.bump_interval,
            'Low Alarm': sensor.low_alarm_threshold,
            'High Alarm': sensor.high_alarm_threshold,
            'Auto Zero': sensor.auto_zero_option,
            'LEL CF': sensor.lel_cf_text,
            'User LEL CF': sensor.user_lel_correction_factor,
            'TWA Alarm': sensor.twa_threshold,
            'STEL Alarm': sensor.stel_threshold,
            'TWA Period': sensor.twa_period,
            'STEL Period': sensor.stel_period,
            'Vol for 50% LEL': sensor.vol_for_50_lel_text,
            'Bump Test Threshold': sensor.bump_test_threshold,
          });
        });

        // 그룹 구분을 위한 빈 행
        excelData.push({});
      });
    } else {
      // Mini
      groupedLogs.forEach(group => {
        if (group.general) {
          excelData.push({
            Time: group.timestamp_formatted,
            Sensor: group.general.sensor,
            'Stealth Mode': group.general.stealth_mode,
            'Safe Mode': group.general.safe_mode,
            'Latching Alarm': group.general.latching_alarm,
            'Confidence Beep': group.general.confidence_beep,
            'Green Light': group.general.green_light,
            'Datalog Interval': group.general.datalog_interval,
            'Confidence Beep Interval': group.general.confidence_beep_interval,
            'Green Light Interval': group.general.green_light_interval,
            BLE: group.general.ble,
            'Low Alarm Ack': group.general.low_alarm_ack,
            '2nd Span Cal': group.general.second_span_cal,
            'Cal Gas Conc': group.general.cal_gas_conc,
            'Cal Interval': group.general.cal_interval,
            'Bump Interval': group.general.bump_interval,
            'STEL Period': group.general.stel_period,
            'TWA Period': group.general.twa_period,
            'Low Alarm': group.general.low_alarm_threshold,
            'High Alarm': group.general.high_alarm_threshold,
            'TWA Alarm': group.general.twa_threshold,
            'STEL Alarm': group.general.stel_threshold,
            'Auto Zero at Startup': group.general.auto_zero_at_startup,
          });
        }
      });
    }

    // 워크시트 생성
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // 워크북 생성
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Configuration Log');

    // 파일 다운로드
    const fileName = `Configuration_Log_${device.serial_number}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <TabLayout>
      <TabHeader dateRange={dateRange} onDateRangeChange={setDateRange} />

      {error && !loading && <ErrorMessage message={error} />}

      {loading ? (
        <LoadingSkeleton count={10} showChart={false} />
      ) : logs.length === 0 ? (
        <EmptyState message="No config logs in selected period" />
      ) : (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing[4],
            width: '100%',
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              backgroundColor: theme.colors.surface.layout.overlay,
              borderRadius: theme.borderRadius.md,
              padding: theme.spacing[6],
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'auto',
              }}
            >
              {isLite ? (
                <table
                  style={{
                    width: 'max-content',
                    minWidth: '100%',
                    borderCollapse: 'collapse',
                    fontSize: theme.fontSize.sm,
                    color: theme.colors.text.primary,
                  }}
                >
                  <tbody>
                    {groupedLogs.map((group, groupIdx) => {
                      const cf = changeFlags[groupIdx];
                      const generalRowCount = group.general ? 2 : 0; // 헤더 + 데이터
                      const sensorRowCount =
                        group.sensors.length > 0 ? group.sensors.length + 1 : 0; // 헤더 + 센서들
                      const totalRowCount = generalRowCount + sensorRowCount;
                      const rows: React.ReactElement[] = [];
                      let isFirstRow = true;

                      // General 섹션
                      if (group.general) {
                        rows.push(
                          <tr
                            key={`group-${groupIdx}-general-header`}
                            style={{
                              borderBottom: `1px solid ${theme.colors.border.container}`,
                              backgroundColor: theme.colors.surface.header.nested,
                            }}
                          >
                            {isFirstRow && (
                              <th
                                rowSpan={totalRowCount}
                                style={{
                                  padding: theme.spacing[3],
                                  textAlign: 'left',
                                  borderRight: `1px solid ${theme.colors.border.container}`,
                                  fontWeight: 600,
                                  minWidth: '180px',
                                  verticalAlign: 'top',
                                  position: 'sticky',
                                  left: 0,
                                  backgroundColor: theme.colors.surface.header.nested,
                                  zIndex: 10,
                                }}
                              >
                                {group.timestamp_formatted}
                              </th>
                            )}
                            <th
                              rowSpan={2}
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                borderRight: `1px solid ${theme.colors.border.container}`,
                                fontWeight: 600,
                                minWidth: '100px',
                                position: 'sticky',
                                left: '180px',
                                backgroundColor: theme.colors.surface.header.nested,
                                zIndex: 10,
                              }}
                            >
                              General
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '120px',
                                position: 'sticky',
                                left: '280px',
                                backgroundColor: theme.colors.surface.header.nested,
                                boxShadow: `inset -2px 0 0 ${theme.colors.border.container}`,
                                zIndex: 10,
                              }}
                            >
                              Worker
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '120px',
                              }}
                            >
                              Location
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '110px',
                              }}
                            >
                              Stealth Mode
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '100px',
                              }}
                            >
                              Safe Mode
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '120px',
                              }}
                            >
                              Latching Alarm
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '130px',
                              }}
                            >
                              Confidence Beep
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '110px',
                              }}
                            >
                              Green Light
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '120px',
                              }}
                            >
                              Datalog Interval
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '160px',
                              }}
                            >
                              Confidence Beep Interval
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '150px',
                              }}
                            >
                              Green Light Interval
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '80px',
                              }}
                            >
                              BLE
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '120px',
                              }}
                            >
                              Low Alarm Ack
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '130px',
                              }}
                            >
                              2nd Span Cal
                            </th>
                          </tr>
                        );
                        isFirstRow = false;

                        rows.push(
                          <tr
                            key={`group-${groupIdx}-general-data`}
                            style={{
                              borderBottom:
                                group.sensors.length === 0
                                  ? `3px solid ${theme.colors.text.secondary}`
                                  : `1px solid ${theme.colors.border.container}`,
                            }}
                          >
                            <td
                              style={{
                                padding: theme.spacing[3],
                                position: 'sticky',
                                left: '280px',
                                backgroundColor: theme.colors.surface.layout.overlay,
                                boxShadow: `inset -2px 0 0 ${theme.colors.border.container}`,
                                borderBottom:
                                  group.sensors.length === 0
                                    ? `3px solid ${theme.colors.text.secondary}`
                                    : `1px solid ${theme.colors.border.container}`,
                                zIndex: 9,
                              }}
                            >
                              {renderValue(group.general.worker_name, cf?.general.worker_name)}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(group.general.location_name, cf?.general.location_name)}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(group.general.stealth_mode, cf?.general.stealth_mode)}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(group.general.safe_mode, cf?.general.safe_mode)}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(group.general.latching_alarm, cf?.general.latching_alarm)}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(group.general.confidence_beep, cf?.general.confidence_beep)}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(group.general.green_light, cf?.general.green_light)}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(group.general.datalog_interval, cf?.general.datalog_interval)}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(
                                group.general.confidence_beep_interval,
                                cf?.general.confidence_beep_interval
                              )}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(
                                group.general.green_light_interval,
                                cf?.general.green_light_interval
                              )}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(group.general.ble, cf?.general.ble)}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(group.general.low_alarm_ack, cf?.general.low_alarm_ack)}
                            </td>
                            <td style={{ padding: theme.spacing[3] }}>
                              {renderValue(group.general.second_span_cal, cf?.general.second_span_cal)}
                            </td>
                          </tr>
                        );
                      }

                      // Sensors 섹션
                      if (group.sensors.length > 0) {
                        rows.push(
                          <tr
                            key={`group-${groupIdx}-sensors-header`}
                            style={{
                              borderBottom: `1px solid ${theme.colors.border.container}`,
                              backgroundColor: theme.colors.surface.header.nested,
                            }}
                          >
                            {isFirstRow && (
                              <th
                                rowSpan={totalRowCount}
                                style={{
                                  padding: theme.spacing[3],
                                  textAlign: 'left',
                                  borderRight: `1px solid ${theme.colors.border.container}`,
                                  fontWeight: 600,
                                  minWidth: '180px',
                                  verticalAlign: 'top',
                                  position: 'sticky',
                                  left: 0,
                                  backgroundColor: theme.colors.surface.header.nested,
                                  zIndex: 10,
                                }}
                              >
                                {group.timestamp_formatted}
                              </th>
                            )}
                            <th
                              rowSpan={sensorRowCount}
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                borderRight: `1px solid ${theme.colors.border.container}`,
                                fontWeight: 600,
                                minWidth: '100px',
                                verticalAlign: 'top',
                                position: 'sticky',
                                left: '180px',
                                backgroundColor: theme.colors.surface.header.nested,
                                zIndex: 10,
                              }}
                            >
                              Sensors
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '80px',
                                position: 'sticky',
                                left: '280px',
                                backgroundColor: theme.colors.surface.header.nested,
                                boxShadow: `inset -2px 0 0 ${theme.colors.border.container}`,
                                zIndex: 10,
                              }}
                            >
                              Sensor
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '90px',
                              }}
                            >
                              Disabled
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '120px',
                              }}
                            >
                              Cal Gas Conc
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '110px',
                              }}
                            >
                              Cal Interval
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '120px',
                              }}
                            >
                              Bump Interval
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '100px',
                              }}
                            >
                              Low Alarm
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '100px',
                              }}
                            >
                              High Alarm
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '100px',
                              }}
                            >
                              Auto Zero
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '120px',
                              }}
                            >
                              LEL CF
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '120px',
                              }}
                            >
                              User LEL CF
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '100px',
                              }}
                            >
                              TWA Alarm
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '110px',
                              }}
                            >
                              STEL Alarm
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '110px',
                              }}
                            >
                              TWA Period
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '110px',
                              }}
                            >
                              STEL Period
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '140px',
                              }}
                            >
                              Vol for 50% LEL
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[3],
                                textAlign: 'left',
                                fontWeight: 600,
                                minWidth: '160px',
                              }}
                            >
                              Bump Test Threshold
                            </th>
                          </tr>
                        );
                        isFirstRow = false;

                        group.sensors.forEach((sensor, sensorIdx) => {
                          const scf = cf?.sensors[sensorIdx];
                          const isLastSensor = sensorIdx === group.sensors.length - 1;
                          const sensorRowBorder = isLastSensor
                            ? `3px solid ${theme.colors.text.secondary}`
                            : `1px solid ${theme.colors.border.container}`;
                          rows.push(
                            <tr
                              key={`group-${groupIdx}-sensor-${sensorIdx}`}
                              style={{ borderBottom: sensorRowBorder }}
                            >
                              <td
                                style={{
                                  padding: theme.spacing[3],
                                  fontWeight: 600,
                                  position: 'sticky',
                                  left: '280px',
                                  backgroundColor: theme.colors.surface.layout.overlay,
                                  boxShadow: `inset -2px 0 0 ${theme.colors.border.container}`,
                                  borderBottom: sensorRowBorder,
                                  zIndex: 9,
                                }}
                              >
                                {sensor.sensor_type_text}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.disabled, scf?.disabled)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.cal_gas_conc, scf?.cal_gas_conc)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.cal_interval, scf?.cal_interval)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.bump_interval, scf?.bump_interval)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.low_alarm_threshold, scf?.low_alarm_threshold)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.high_alarm_threshold, scf?.high_alarm_threshold)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.auto_zero_option, scf?.auto_zero_option)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.lel_cf_text, scf?.lel_cf_text)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(
                                  sensor.user_lel_correction_factor,
                                  scf?.user_lel_correction_factor
                                )}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.twa_threshold, scf?.twa_threshold)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.stel_threshold, scf?.stel_threshold)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.twa_period, scf?.twa_period)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.stel_period, scf?.stel_period)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.vol_for_50_lel_text, scf?.vol_for_50_lel_text)}
                              </td>
                              <td style={{ padding: theme.spacing[3] }}>
                                {renderValue(sensor.bump_test_threshold, scf?.bump_test_threshold)}
                              </td>
                            </tr>
                          );
                        });
                      }

                      return <React.Fragment key={`group-${groupIdx}`}>{rows}</React.Fragment>;
                    })}
                  </tbody>
                </table>
              ) : (
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: theme.fontSize.sm,
                    color: theme.colors.text.primary,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        borderBottom: `2px solid ${theme.colors.border.container}`,
                        backgroundColor: theme.colors.surface.header.nested,
                      }}
                    >
                      <th
                        style={{
                          padding: theme.spacing[3],
                          textAlign: 'left',
                          position: 'sticky',
                          left: 0,
                          backgroundColor: theme.colors.surface.header.nested,
                          zIndex: 10,
                          minWidth: '180px',
                        }}
                      >
                        Time
                      </th>
                      <th
                        style={{
                          padding: theme.spacing[3],
                          textAlign: 'left',
                          position: 'sticky',
                          left: '180px',
                          backgroundColor: theme.colors.surface.header.nested,
                          boxShadow: `inset -2px 0 0 ${theme.colors.border.container}`,
                          zIndex: 10,
                          minWidth: '100px',
                        }}
                      >
                        Sensor
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '110px' }}
                      >
                        Stealth Mode
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '100px' }}
                      >
                        Safe Mode
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '120px' }}
                      >
                        Latching Alarm
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '130px' }}
                      >
                        Confidence Beep
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '110px' }}
                      >
                        Green Light
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '130px' }}
                      >
                        Datalog Interval
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '165px' }}
                      >
                        Confidence Beep Interval
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '155px' }}
                      >
                        Green Light Interval
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '60px' }}
                      >
                        BLE
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '120px' }}
                      >
                        Low Alarm Ack
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '120px' }}
                      >
                        2nd Span Cal
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '120px' }}
                      >
                        Cal Gas Conc
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '110px' }}
                      >
                        Cal Interval
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '120px' }}
                      >
                        Bump Interval
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '110px' }}
                      >
                        STEL Period
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '100px' }}
                      >
                        TWA Period
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '100px' }}
                      >
                        Low Alarm
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '100px' }}
                      >
                        High Alarm
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '100px' }}
                      >
                        TWA Alarm
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '110px' }}
                      >
                        STEL Alarm
                      </th>
                      <th
                        style={{ padding: theme.spacing[3], textAlign: 'left', minWidth: '160px' }}
                      >
                        Auto Zero at Startup
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedLogs.map((group, idx) => {
                      const cf = changeFlags[idx];
                      return (
                      <tr
                        key={`mini-${idx}`}
                        style={{ borderBottom: `1px solid ${theme.colors.border.container}` }}
                      >
                        <td
                          style={{
                            padding: theme.spacing[3],
                            position: 'sticky',
                            left: 0,
                            backgroundColor: theme.colors.surface.layout.overlay,
                            borderBottom: `1px solid ${theme.colors.border.container}`,
                            zIndex: 9,
                          }}
                        >
                          {group.timestamp_formatted}
                        </td>
                        <td
                          style={{
                            padding: theme.spacing[3],
                            position: 'sticky',
                            left: '180px',
                            backgroundColor: theme.colors.surface.layout.overlay,
                            boxShadow: `inset -2px 0 0 ${theme.colors.border.container}`,
                            borderBottom: `1px solid ${theme.colors.border.container}`,
                            zIndex: 9,
                          }}
                        >
                          {group.general?.sensor || '-'}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.stealth_mode || '-', cf?.general.stealth_mode)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.safe_mode || '-', cf?.general.safe_mode)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.latching_alarm || '-', cf?.general.latching_alarm)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.confidence_beep || '-', cf?.general.confidence_beep)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.green_light || '-', cf?.general.green_light)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.datalog_interval || '-', cf?.general.datalog_interval)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(
                            group.general?.confidence_beep_interval || '-',
                            cf?.general.confidence_beep_interval
                          )}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(
                            group.general?.green_light_interval || '-',
                            cf?.general.green_light_interval
                          )}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.ble || '-', cf?.general.ble)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.low_alarm_ack || '-', cf?.general.low_alarm_ack)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.second_span_cal || '-', cf?.general.second_span_cal)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.cal_gas_conc || '-', cf?.general.cal_gas_conc)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.cal_interval || '-', cf?.general.cal_interval)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.bump_interval || '-', cf?.general.bump_interval)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.stel_period || '-', cf?.general.stel_period)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.twa_period || '-', cf?.general.twa_period)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(
                            group.general?.low_alarm_threshold || '-',
                            cf?.general.low_alarm_threshold
                          )}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(
                            group.general?.high_alarm_threshold || '-',
                            cf?.general.high_alarm_threshold
                          )}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.twa_threshold || '-', cf?.general.twa_threshold)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(group.general?.stel_threshold || '-', cf?.general.stel_threshold)}
                        </td>
                        <td style={{ padding: theme.spacing[3] }}>
                          {renderValue(
                            group.general?.auto_zero_at_startup || '-',
                            cf?.general.auto_zero_at_startup
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <DownloadButtons
            onDownloadExcel={handleExcelDownload}
            disabled={loading || logs.length === 0}
          />
        </div>
      )}
    </TabLayout>
  );
};
